import { ref, computed, type Ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { Assignment } from '@/composables/useAssignments'
import type { Project } from '@/composables/useProjects'
import type { Worker } from '@/composables/useWorkers'

export interface Job {
  id?: number
  name: string
  completed: boolean
  project: number
  worker: number
  created: string
  args: string
  [key: string]: boolean | string | number | object | string[] | null | undefined
}

export type WorkerControlJobName = 'promote_to_leader' | 'enter_recovery_mode' | 'worker_restart_requested'

export interface WorkerControlOption {
  label: string
  value: WorkerControlJobName
}

interface UseWorkerControlJobsOptions {
  assignments: Ref<Assignment[]>
  jobs: Ref<Job[]>
  projects: Ref<Project[]>
  fetchJobs: () => Promise<void>
}

const workerControlOptions: WorkerControlOption[] = [
  { label: 'Promote To Leader', value: 'promote_to_leader' },
  { label: 'Enter Recovery Mode', value: 'enter_recovery_mode' },
  { label: 'Restart Worker', value: 'worker_restart_requested' }
]

export function useJobs() {
  const jobs = ref<Job[]>([]) // items is now Ref<Job[]>
  let inFlightFetch: Promise<void> | null = null

  const getJobs = async (): Promise<Job[]> => {
    const response = await fetch('/api/jobs/')
    // Assert the response is OK
    if (!response.ok) {
      throw new Error('Failed to fetch Jobs')
    }
    // Assert the response is JSON
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new Error('Invalid content type')
    }

    return await response.json()
  }

  const fetchJobs = async () => {
    if (inFlightFetch) {
      return inFlightFetch
    }

    inFlightFetch = (async () => {
      jobs.value = await getJobs()
    })().finally(() => {
      inFlightFetch = null
    })

    return inFlightFetch
  }

  const jobKeys = computed(() => {
    const keys = jobs.value.length > 0 ? Object.keys(jobs.value[0]) : []
    // remove created and updated keys
    keys.splice(keys.indexOf('created'), 1)
    return keys
  })

  return { jobs, fetchJobs, jobKeys }
}

export function useWorkerControlJobs(options: UseWorkerControlJobsOptions) {
  const authStore = useAuthStore()
  const workerControlSelection = ref<Record<number, WorkerControlJobName>>({})
  const workerControlSubmitting = ref<Record<number, boolean>>({})

  const getAssignmentByWorkerId = (workerId: unknown) => {
    const id = Number(workerId)
    if (!Number.isFinite(id)) {
      return null
    }

    return options.assignments.value.find((assignment) => Number(assignment?.worker) === id) ?? null
  }

  const getJobById = (jobId: unknown) => {
    const id = Number(jobId)
    if (!Number.isFinite(id)) {
      return null
    }

    return options.jobs.value.find((job) => Number(job.id) === id) ?? null
  }

  const getFallbackProjectId = () => {
    const project = options.projects.value.find((item) => {
      const id = Number(item?.id)
      return Number.isFinite(id) && id > 0
    })

    if (!project) {
      return null
    }

    const projectId = Number(project.id)
    return Number.isFinite(projectId) && projectId > 0 ? projectId : null
  }

  const hasWorkerAssignment = (worker: Worker) => {
    return getAssignmentByWorkerId(worker.id) != null
  }

  const getWorkerControlProjectId = (worker: Worker) => {
    const assignment = getAssignmentByWorkerId(worker.id)
    const job = getJobById(assignment?.job)
    const projectId = Number(job?.project)
    if (Number.isFinite(projectId) && projectId > 0) {
      return projectId
    }

    return getFallbackProjectId()
  }

  const getWorkerControlSelection = (worker: Worker) => {
    const workerId = Number(worker.id)
    if (!Number.isFinite(workerId)) {
      return workerControlOptions[0].value
    }

    return workerControlSelection.value[workerId] ?? workerControlOptions[0].value
  }

  const setWorkerControlSelection = (worker: Worker, value: string) => {
    const workerId = Number(worker.id)
    if (!Number.isFinite(workerId)) {
      return
    }

    const option = workerControlOptions.find((item) => item.value === value)
    if (!option) {
      return
    }

    workerControlSelection.value = {
      ...workerControlSelection.value,
      [workerId]: option.value
    }
  }

  const getWorkerControlLabel = (worker: Worker) => {
    const selection = getWorkerControlSelection(worker)
    return workerControlOptions.find((option) => option.value === selection)?.label ?? 'Promote To Leader'
  }

  const isWorkerControlSubmitting = (worker: Worker) => {
    const workerId = Number(worker.id)
    if (!Number.isFinite(workerId)) {
      return false
    }

    return workerControlSubmitting.value[workerId] === true
  }

  const submitWorkerControlJob = async (worker: Worker) => {
    const workerId = Number(worker.id)
    if (!Number.isFinite(workerId) || workerId <= 0) {
      alert('Cannot send control signal: missing worker id.')
      return
    }

    const projectId = getWorkerControlProjectId(worker)
    if (projectId == null) {
      alert('Cannot send control signal: no valid project is available.')
      return
    }

    const csrfToken = await authStore.getCsrfToken()
    const jobName = getWorkerControlSelection(worker)

    workerControlSubmitting.value = {
      ...workerControlSubmitting.value,
      [workerId]: true
    }

    try {
      const response = await fetch('/api/jobs/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken ?? ''
        },
        body: JSON.stringify({
          name: jobName,
          project: String(projectId),
          worker: String(workerId),
          target: '1',
          args: ''
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to create control job ${jobName}`)
      }

      await options.fetchJobs()
    } catch (e) {
      console.error(e)
      alert(`Failed to send ${getWorkerControlLabel(worker)}.`)
    } finally {
      workerControlSubmitting.value = {
        ...workerControlSubmitting.value,
        [workerId]: false
      }
    }
  }

  return {
    workerControlOptions,
    hasWorkerAssignment,
    getAssignmentByWorkerId,
    getWorkerControlProjectId,
    getWorkerControlSelection,
    setWorkerControlSelection,
    getWorkerControlLabel,
    isWorkerControlSubmitting,
    submitWorkerControlJob
  }
}